import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Alert,
  Link,
  InputAdornment,
  CircularProgress,
  Snackbar,
  IconButton,
  Button,
} from '@mui/material';
import {
  PhoneOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import AuthCard from '../components/auth/AuthCard';
import AuthPrimaryButton from '../components/auth/AuthPrimaryButton';
import { formContainer, formField, stepFade } from '../components/auth/animations';

/**
 * ForgotPasswordPage
 * Step 1: phone input → send OTP via WhatsApp.
 * Step 2: OTP + new password → reset.
 * Smooth AnimatePresence fade between steps.
 * Uses AuthLayout (DesktopNav + AuthBackground) as the route-level shell.
 */
function ForgotPasswordPage() {
  const navigate = useNavigate();

  // Step: 1 = phone input, 2 = OTP + new password
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState('');
  const [otpDeliveryWarning, setOtpDeliveryWarning] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const normalizePhone = (p) => {
    if (!p) return '';
    // eslint-disable-next-line no-useless-escape
    let cleaned = p.replace(/[\s\-\(\)]/g, '');
    if (!cleaned.startsWith('+')) {
      if (cleaned.startsWith('91') && cleaned.length === 12) {
        cleaned = '+' + cleaned;
      } else if (cleaned.length === 10) {
        cleaned = '+91' + cleaned;
      }
    }
    return cleaned;
  };

  const isValidPhone = (p) => {
    const normalized = normalizePhone(p);
    return /^\+91\d{10}$/.test(normalized);
  };

  const handleSendOtp = async () => {
    if (!phone) {
      setError('Please enter your phone number');
      return;
    }
    if (!isValidPhone(phone)) {
      setError('Please enter a valid 10-digit Indian phone number');
      return;
    }

    setSendingOtp(true);
    setError('');

    try {
      const res = await api.post('/auth/send-otp', {
        phone,
        purpose: 'password_reset',
      });
      setStep(2);
      setResendCooldown(60);
      const delivered = res.data?.data?.delivered;
      if (delivered === false) {
        setOtpDeliveryWarning(true);
        setSuccessMessage('');
      } else {
        setOtpDeliveryWarning(false);
        setSuccessMessage('OTP sent to your WhatsApp!');
      }
    } catch (err) {
      const errorCode = err.response?.data?.code;
      if (errorCode === 'PHONE_NOT_FOUND') {
        setError('No account found with this phone number.');
      } else if (errorCode === 'COOLDOWN') {
        setResendCooldown(err.response?.data?.retryAfter || 60);
        setError(`Please wait ${err.response?.data?.retryAfter || 60} seconds before requesting a new OTP`);
      } else if (errorCode === 'TOO_MANY_REQUESTS') {
        setError('Too many OTP requests. Please try again later.');
      } else {
        setError(err.response?.data?.message || 'Failed to send OTP');
      }
    } finally {
      setSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    setSendingOtp(true);
    setError('');

    try {
      const res = await api.post('/auth/send-otp', {
        phone,
        purpose: 'password_reset',
      });
      setResendCooldown(60);
      const delivered = res.data?.data?.delivered;
      if (delivered === false) {
        setOtpDeliveryWarning(true);
        setSuccessMessage('');
      } else {
        setOtpDeliveryWarning(false);
        setSuccessMessage('OTP resent to your WhatsApp!');
      }
    } catch (err) {
      const errorCode = err.response?.data?.code;
      if (errorCode === 'COOLDOWN') {
        setResendCooldown(err.response?.data?.retryAfter || 60);
        setError(`Please wait ${err.response?.data?.retryAfter || 60} seconds`);
      } else {
        setError(err.response?.data?.message || 'Failed to resend OTP');
      }
    } finally {
      setSendingOtp(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setResetting(true);

    try {
      await api.post('/auth/reset-password', {
        phone,
        otp,
        new_password: newPassword,
      });
      setSuccessMessage('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const errorCode = err.response?.data?.code;
      if (errorCode === 'INVALID_OTP') {
        setError(err.response?.data?.message || 'Invalid OTP');
      } else if (errorCode === 'OTP_EXPIRED') {
        setError('OTP has expired. Please request a new one.');
        setStep(1);
      } else if (errorCode === 'MAX_ATTEMPTS') {
        setError('Maximum attempts exceeded. Please request a new OTP.');
        setStep(1);
      } else {
        setError(err.response?.data?.message || 'Failed to reset password');
      }
    } finally {
      setResetting(false);
    }
  };

  const subtitle =
    step === 1
      ? 'Enter your registered phone number to receive an OTP'
      : 'Enter the OTP and your new password';

  return (
    <>
      <AuthCard
        title="Reset Password"
        subtitle={subtitle}
        maxWidth={440}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* AnimatePresence fade between Step 1 and Step 2 */}
        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div
              key="step-1"
              initial={stepFade.initial}
              animate={stepFade.animate}
              exit={stepFade.exit}
            >
              <motion.div variants={formContainer} initial="initial" animate="animate">
                {/* Phone field */}
                <motion.div variants={formField}>
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="9876543210"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneOutlined color="action" />
                            <Typography sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 500 }}>+91</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </motion.div>

                <motion.div variants={formField}>
                  <AuthPrimaryButton
                    type="button"
                    loading={sendingOtp}
                    disabled={!isValidPhone(phone)}
                    onClick={handleSendOtp}
                  >
                    {sendingOtp ? 'Sending…' : 'Send OTP via WhatsApp'}
                  </AuthPrimaryButton>
                </motion.div>
              </motion.div>
            </motion.div>
          ) : (
            <motion.form
              key="step-2"
              onSubmit={handleResetPassword}
              initial={stepFade.initial}
              animate={stepFade.animate}
              exit={stepFade.exit}
            >
              <motion.div variants={formContainer} initial="initial" animate="animate">
                {/* Delivery warning — shown when OTP was generated but WhatsApp delivery failed */}
                {otpDeliveryWarning && (
                  <motion.div variants={formField}>
                    <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                      We couldn't deliver the code via WhatsApp just now. If you don't receive it, tap Resend in a few seconds.
                    </Alert>
                  </motion.div>
                )}
                {/* OTP field */}
                <motion.div variants={formField}>
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Enter OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputProps={{
                        maxLength: 6,
                        style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.2rem' },
                      }}
                      helperText={
                        <Box component="span" display="flex" justifyContent="space-between" alignItems="center">
                          <span>6-digit OTP sent to your WhatsApp</span>
                          <Button
                            variant="text"
                            size="small"
                            onClick={handleResendOtp}
                            disabled={sendingOtp || resendCooldown > 0}
                            sx={{ minWidth: 'auto', p: 0, textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            {resendCooldown > 0
                              ? `Resend in ${resendCooldown}s`
                              : sendingOtp
                              ? <CircularProgress size={12} />
                              : 'Resend OTP'}
                          </Button>
                        </Box>
                      }
                    />
                  </Box>
                </motion.div>

                {/* New password */}
                <motion.div variants={formField}>
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      helperText="Minimum 6 characters"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </motion.div>

                {/* Confirm password */}
                <motion.div variants={formField}>
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      error={confirmPassword !== '' && newPassword !== confirmPassword}
                      helperText={
                        confirmPassword !== '' && newPassword !== confirmPassword
                          ? 'Passwords do not match'
                          : ''
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </motion.div>

                <motion.div variants={formField}>
                  <AuthPrimaryButton
                    type="submit"
                    loading={resetting}
                    disabled={otp.length !== 6 || !newPassword || newPassword !== confirmPassword}
                  >
                    {resetting ? 'Resetting…' : 'Reset Password'}
                  </AuthPrimaryButton>
                </motion.div>
              </motion.div>
            </motion.form>
          )}
        </AnimatePresence>

        <Box mt={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Remember your password?{' '}
            <Link
              component={RouterLink}
              to="/login"
              underline="none"
              sx={{ color: 'primary.main', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}
            >
              Back to Login
            </Link>
          </Typography>
        </Box>
      </AuthCard>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          variant="filled"
          sx={{ width: '100%', fontWeight: 600, fontSize: '1rem' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ForgotPasswordPage;
